/**
 * Custom Fetch Mutator for Orval
 *
 * This module provides the custom fetcher used by all Orval-generated API calls.
 * It handles:
 * - Base URL configuration from environment
 * - Authorization header injection
 * - Default headers (Accept, Content-Type)
 * - Consistent error handling with typed errors
 */

import { getAuthHeader } from './auth';

/**
 * API base URL from environment or default
 */
const API_BASE_URL =
  typeof window !== 'undefined' &&
  (window as unknown as { __VITE_API_BASE_URL__?: string })
    .__VITE_API_BASE_URL__
    ? (window as unknown as { __VITE_API_BASE_URL__?: string })
        .__VITE_API_BASE_URL__
    : import.meta.env?.VITE_API_BASE_URL ||
      'https://mydynasty.ca/ext/api/v1/surveys';

/**
 * Custom API error class with typed properties
 */
export class ApiError extends Error {
  public readonly status: number;
  public readonly statusText: string;
  public readonly body: unknown;
  public readonly url: string;

  constructor(
    message: string,
    status: number,
    statusText: string,
    body: unknown,
    url: string,
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.statusText = statusText;
    this.body = body;
    this.url = url;
  }

  /**
   * Check if this is an authentication error
   */
  isAuthError(): boolean {
    return this.status === 401 || this.status === 403;
  }

  /**
   * Check if this is a not found error
   */
  isNotFound(): boolean {
    return this.status === 404;
  }

  /**
   * Check if this is a validation error
   */
  isValidationError(): boolean {
    return this.status === 400 || this.status === 422;
  }

  /**
   * Check if this is a server error
   */
  isServerError(): boolean {
    return this.status >= 500;
  }
}

/**
 * Extended RequestInit with body as unknown for JSON serialization
 * Exported for use in generated code
 */
export type RequestOptions = Omit<RequestInit, 'body'> & {
  body?: unknown;
};

/**
 * Custom fetch function for Orval
 *
 * This is the mutator that Orval uses for all generated API calls.
 * It automatically handles:
 * - Base URL resolution
 * - Request body serialization
 * - Authorization header injection
 * - Response parsing
 * - Error handling
 *
 * @param url - The API endpoint path (may include query string)
 * @param options - Request options (method, headers, body, etc.)
 * @returns Promise resolving to the response data
 */
export async function customFetch<T>(
  url: string,
  options: RequestOptions = {},
): Promise<T> {
  const { body, headers = {}, ...restOptions } = options;

  // Build full URL with base
  const fullUrl = `${API_BASE_URL}${url}`;

  // Build headers
  const requestHeaders: Record<string, string> = {
    Accept: 'application/json',
    ...getAuthHeader(),
    ...(headers as Record<string, string>),
  };

  // Add Content-Type for requests with body
  if (body !== undefined) {
    requestHeaders['Content-Type'] = 'application/json';
  }

  // Make the request
  const response = await fetch(fullUrl, {
    ...restOptions,
    headers: requestHeaders,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  // Parse response body
  let responseBody: unknown;
  const contentType = response.headers.get('content-type');

  if (contentType?.includes('application/json')) {
    try {
      responseBody = await response.json();
    } catch {
      responseBody = null;
    }
  } else {
    responseBody = await response.text();
  }

  // Handle errors
  if (!response.ok) {
    const errorMessage =
      typeof responseBody === 'object' &&
      responseBody !== null &&
      'message' in responseBody
        ? String((responseBody as { message: unknown }).message)
        : typeof responseBody === 'object' &&
            responseBody !== null &&
            'error' in responseBody
          ? String((responseBody as { error: unknown }).error)
          : `Request failed with status ${response.status}`;

    throw new ApiError(
      errorMessage,
      response.status,
      response.statusText,
      responseBody,
      fullUrl,
    );
  }

  return responseBody as T;
}

/**
 * Export default for Orval mutator compatibility
 */
export default customFetch;
